// Design tokens for the Synthesis Visualizations module.
// Values are taken verbatim from the hi-fi handoff so the module renders
// pixel-accurately without depending on Tailwind config specifics.

export const color = {
  purple:        '#7C3AED',
  purpleDeep:    '#6D28D9',
  purpleGrad:    'linear-gradient(90deg, #8B5CF6, #7C3AED)',
  iconGrad:      'linear-gradient(135deg, #EDE9FE, #DDD6FE)',
  pillActiveBg:  '#F5F3FF',
  pillMutedBg:   '#F1F5F9',
  chipBorder:    '#E9D5FF',

  ink:           '#0F172A',
  text:          '#334155',
  textMuted:     '#64748B',
  textFaint:     '#94A3B8',
  hairline:      '#E2E8F0',
  separator:     '#CBD5E1',
  surface:       '#FFFFFF',
  surfaceAlt:    '#FCFCFD',
  surfaceHover:  '#F8FAFC',

  green:         '#10B981',
  methodology:   '#475569',
} as const;

export type SeverityBand = 'HIGH' | 'MED-HIGH' | 'MED' | 'LOW';

export const severityBand: Record<SeverityBand, { bg: string; fg: string; border: string }> = {
  'HIGH':     { bg: '#FEE2E2', fg: '#DC2626', border: '#FCA5A5' },
  'MED-HIGH': { bg: '#FEF3C7', fg: '#B45309', border: '#FCD34D' },
  'MED':      { bg: '#DBEAFE', fg: '#1D4ED8', border: '#93C5FD' },
  'LOW':      { bg: '#F1F5F9', fg: '#64748B', border: '#CBD5E1' },
};

export const mono =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

// Weighted severity: customer impact · effort to work around · occurrence
// frequency · blast radius. Each dimension is 1–5. Kept here so the tab and any
// future real-data adapter agree on one formula.
export const SEVERITY_WEIGHTS = {
  customer: 0.35,
  effort:   0.15,
  frequency: 0.25,
  blast:    0.25,
} as const;

export function computeSeverity(d: {
  customer: number; effort: number; frequency: number; blast: number;
}): number {
  const s =
    d.customer * SEVERITY_WEIGHTS.customer +
    d.effort   * SEVERITY_WEIGHTS.effort +
    d.frequency * SEVERITY_WEIGHTS.frequency +
    d.blast    * SEVERITY_WEIGHTS.blast;
  return Math.round(s * 10) / 10;
}

export function bandFor(severity: number): SeverityBand {
  if (severity >= 4.0) return 'HIGH';
  if (severity >= 3.5) return 'MED-HIGH';
  if (severity >= 2.8) return 'MED';
  return 'LOW';
}
